// src/hooks/useUploadGuard.js
//
// While `active` is true (a file is being uploaded):
// * closing or reloading the tab asks for confirmation (beforeunload);
// * the screen is kept awake where the Screen Wake Lock API exists, so a phone
//   does not lock itself and suspend the upload (audit FL-10).
import { useEffect } from 'react';

export default function useUploadGuard(active) {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      // Older browsers need returnValue; the text itself is not shown anymore.
      event.returnValue = 'Probíhá nahrávání souboru. Opravdu chcete stránku opustit?';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    let lock = null;
    let stopped = false;
    const acquire = async () => {
      try {
        if (!navigator.wakeLock || document.visibilityState !== 'visible') return;
        const sentinel = await navigator.wakeLock.request('screen');
        if (stopped) {
          sentinel.release().catch(() => {});
        } else {
          lock = sentinel;
        }
      } catch (e) {
        /* not supported or refused (e.g. battery saver) - nothing else to do */
      }
    };
    // The lock is released automatically when the tab is hidden; take it again on return.
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && (!lock || lock.released)) acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopped = true;
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      if (lock && !lock.released) lock.release().catch(() => {});
    };
  }, [active]);
}
